import { Component, Injector, OnInit, ViewChild, inject } from "@angular/core";
import { GetIconByModuleString } from "@methods/Icons";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { TabModel } from "../../../common/models/generic/TabModels/TabModel";
import { StandingdataDataService } from "../shared/standingdata.service";
import { TabMenu } from "@models/generic/TabModels/TabMenu";
import { StandingDataGenericComponent } from "../generic/standingdata-generic.component";
import { MicroservicePermissionService } from "@services/permissions/permissions.service";
import { StandingDataListComponent } from "@app/standing-data/standing-data-list/standing-data-list.component";
import { BaseStandingDataService } from "@app/standing-data/interfaces/base-standing-data.service";
import { IncidentTypesService } from "@app/incident/services/incident-types.service";
import { IncidentClassificationService } from "@app/incident/services/incident-classification.service";
import { MicroserviceModule } from "@enums/microservice-module.enum";
import { IncidentPermissionType } from "@enums/permissions/incident-permission-type.enum";
import { TranslocoPipe, TranslocoService, provideTranslocoScope } from "@jsverse/transloco";
import { firstValueFrom } from "rxjs";
import { NgComponentOutlet, NgTemplateOutlet } from "@angular/common";
import { CerrixLoaderComponent } from "@app/shared/components/cerrix-loader/cerrix-loader.component";
import { CerrixIconViewerComponent } from "@app/shared/components/cerrix-icon-viewer/cerrix-icon-viewer.component";

@Component({
    selector: "app-standingdata-overview",
    templateUrl: "./standingdata-overview.component.html",
    styleUrls: ["./standingdata-overview.component.scss"],
    imports: [
        TranslocoPipe,
        NgComponentOutlet,
        NgTemplateOutlet,
        CerrixLoaderComponent,
        CerrixIconViewerComponent,
        StandingDataGenericComponent,
    ],
    providers: [provideTranslocoScope("standingdata")]
})
export class StandingDataOverviewComponent implements OnInit {
    private _sdService = inject(StandingdataDataService);
    private _msPermService = inject(MicroservicePermissionService);
    private _injector = inject(Injector);
    private _transloco = inject(TranslocoService);

    @ViewChild("customMenu", { static: true }) customMenu: any;
    @ViewChild("genericSD", { static: false }) genericSD: StandingDataGenericComponent;

    tab: TabModel;
    sortedModules = [];

    allowedPages: CerrixTreeItem[];
    selectedPage: CerrixTreeItem;

    sdConfig: { type: any, hasCustomComponent: boolean, customComponent?: any };

    protected customComponentKey = "customSdComponent";

    async ngOnInit() {
        this.tab.menu = new TabMenu();
        this.tab.menu.customMenu = this.customMenu;
        if (!this.tab.showLoader) {
            this.tab.showLoader = true;
        }

        // Load the scope up front: the incident nodes below are named from TS, not through a
        // pipe, so the scope must already be loaded before the first `translate()` call.
        // `load()` only auto-appends the active language when the path is already scope-shaped
        // ("scope/lang"), so a bare scope name here would request the scope with no language
        // and silently return no translations.
        await firstValueFrom(
            this._transloco.load(`standingdata/${this._transloco.getActiveLang()}`)
        );

        this._sdService.getStandingDataOverview().subscribe((data) => {
            data.forEach((node) => {
                const moduleIcon = GetIconByModuleString(node.Name);
                if (moduleIcon) {
                    node.Icon = JSON.stringify({ selector: moduleIcon });
                }
            });

            const allData = this.addMicroserviceStandingData(data);
            this.allowedPages = allData;
            this.loadFromID(this.allowedPages);
            this.tab.showLoader = false;
        });
    }

    loadFromID(nodes: CerrixTreeItem[]) {
        let found = false;
        if (this.tab.id) {
            let i = 0;

            while (!found && i < nodes.length) {
                const node = nodes[i];
                if ("" + node.ID === "" + this.tab.id) {
                    this.nodeClicked(node);
                    found = true;
                } else if (node.Children.length > 0) {
                    found = this.loadFromID(node.Children);
                    node.isExpanded = found;
                }
                i++;
            }
        }

        return found;
    }

    async nodeClicked(node: CerrixTreeItem) {
        if (node.Children.length === 0) {
            if (!this.selectedPage || this.selectedPage.ID !== node.ID) {
                if (this.genericSD && !(await this.genericSD.editCmp.dirtyCheck())) return;

                this.selectedPage = node;
                this.tab.id = "" + node.ID;

                this.sdConfig = null;
                this.loadConfig(this.selectedPage.ID, this.selectedPage[this.customComponentKey]);
                this.tab.name = this._transloco.translate("standingdata.overview.tabTitle", {
                    name: node.Name
                });
            }
        } else {
            node.isExpanded = !node.isExpanded;
        }
    }

    loadConfig(pageID, customComponent?: any) {
        setTimeout(() => {
            this.sdConfig = {
                type: pageID,
                hasCustomComponent: !!customComponent,
                customComponent: customComponent
            };
        }, 1000);
    }

    private addMicroserviceStandingData(data: CerrixTreeItem[]): CerrixTreeItem[] {
        data = this.addIncidentStandingData(data);
        return data;
    }

    private addIncidentStandingData(data: CerrixTreeItem[]): CerrixTreeItem[] {
        const incidentNode = <CerrixTreeItem>{
            ID: 100,
            Name: this._transloco.translate("standingdata.overview.incidentsNodeName"),
            Icon: JSON.stringify({ selector: "fas fa-bolt" }),
            Children: [],
            ParentID: null,
            ID_Path: null,
        };

        // Classification
        const classificationPermission = this._msPermService.hasPermission(MicroserviceModule.Incidents, IncidentPermissionType.ClassificationStandingData);
        if (classificationPermission) {
            const classificationNode = <CerrixTreeItem>{
                ID: 101,
                Name: this._transloco.translate("standingdata.overview.classificationNodeName"),
                Icon: JSON.stringify({ selector: "fas fa-tags" }),
                Children: [],
                ParentID: incidentNode.ID,
                ID_Path: null,
            };
            classificationNode[this.customComponentKey] = {
                component: StandingDataListComponent,
                inputs: {
                    title: this._transloco.translate("standingdata.overview.classificationTitle"),
                },
                injector: Injector.create({
                    providers: [
                        { provide: BaseStandingDataService, useClass: IncidentClassificationService },
                    ],
                    parent: this._injector,
                })
            };
            incidentNode.Children.push(classificationNode);
        }
        
        // Incident types
        const incidentTypesPermission = this._msPermService.hasPermission(MicroserviceModule.Incidents, IncidentPermissionType.IncidentTypeStandingData);
        if (incidentTypesPermission) {
            const typeNode = <CerrixTreeItem>{
                ID: 102,
                Name: this._transloco.translate("standingdata.overview.incidentTypesNodeName"),
                Icon: JSON.stringify({ selector: "fas fa-th-list" }),
                Children: [],
                ParentID: incidentNode.ID,
                ID_Path: null,
            };
            typeNode[this.customComponentKey] = {
                component: StandingDataListComponent,
                inputs: {
                    title: this._transloco.translate("standingdata.overview.incidentTypesTitle"),
                },
                injector: Injector.create({
                    providers: [
                        { provide: BaseStandingDataService, useClass: IncidentTypesService },
                    ],
                    parent: this._injector,
                })
            };
            incidentNode.Children.push(typeNode);
        }

        if (incidentNode.Children.length > 0) {
            const index = this.getFirstFoundItemIndex(data, ["Events", "Controls", "Risks", "General"]);
            // We insert the incident node after the first found item of the above list, if any. Otherwise, we add it to the start of the list.
            data.splice(index >= 0 ? index + 1 : 0, 0, incidentNode);
        }
        return data;
    }

    private getFirstFoundItemIndex(items: CerrixTreeItem[], names: string[]): number {
        for (const name of names) {
            const index = items.findIndex(item => item.Name === name);
            if (index !== -1) {
                return index;
            }
        }
        return -1;
    }
}
